import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  Moon,
  RefreshCw,
  FileText,
  BarChart3,
  Calendar,
  Phone,
  CheckCircle
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function IslamicFinanceGuide() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#0f172a] to-[#020617] text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        <Link to="/bio/zahirul" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Profile
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Moon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">🕌 Islamic Home Finance</h1>
          <p className="text-slate-400">Thames Mortgage & Protection</p>
          <p className="text-lg text-emerald-400 mt-4">Ethical, Sharia-compliant property finance — structured correctly and transparently.</p>
        </motion.div>

        {/* Islamic Home Purchase Plans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 backdrop-blur border border-emerald-500/30 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Home className="w-5 h-5 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold">🏠 Islamic Home Purchase Plans</h2>
          </div>
          <p className="text-slate-300 mb-4">We work with specialist UK-regulated Islamic finance providers offering:</p>
          <ul className="space-y-2 text-slate-300 mb-4">
            <li>• <strong className="text-white">Ijara</strong> (lease-based structure)</li>
            <li>• <strong className="text-white">Diminishing Musharaka</strong> (co-ownership model)</li>
            <li>• No interest (Riba-free)</li>
            <li>• Clear and transparent profit rates</li>
            <li>• Fully Sharia-compliant UK agreements</li>
          </ul>
          <p className="text-emerald-400 font-medium mb-2">Suitable for:</p>
          <ul className="space-y-1 text-slate-300">
            <li>• First-time buyers</li>
            <li>• Home movers</li>
            <li>• Switching from conventional mortgages</li>
            <li>• Buy-to-let (selected providers)</li>
          </ul>
        </motion.div>

        {/* Switching from Conventional */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold">🔁 Switching from a Conventional Mortgage</h2>
          </div>
          <p className="text-slate-300 mb-4">Already have a standard mortgage? We guide you through the full transition:</p>
          <ul className="space-y-2 text-slate-300">
            <li>• Review your existing mortgage and early repayment charges</li>
            <li>• Redeem the conventional loan</li>
            <li>• Move to a Sharia-compliant structure</li>
            <li>• Assess affordability under Islamic finance criteria</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">Handled carefully, from start to completion.</p>
        </motion.div>

        {/* Deposit & Eligibility */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold">🧾 Deposit & Eligibility Guidance</h2>
          </div>
          <p className="text-slate-300 mb-4">Clear guidance before you proceed:</p>
          <ul className="space-y-2 text-slate-300">
            <li>• Typical minimum deposits from 5–10%</li>
            <li>• Income assessed under UK affordability rules</li>
            <li>• Credit history still considered</li>
            <li>• Gifted deposits accepted (subject to provider criteria)</li>
          </ul>
          <p className="text-amber-400 mt-4 font-medium">No confusion — just clarity.</p>
        </motion.div>

        {/* Why Specialist Advice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="text-xl font-semibold">📊 Why Specialist Advice Matters</h2>
          </div>
          <p className="text-slate-300 mb-4">Islamic home finance differs fundamentally from conventional mortgages.</p>
          <p className="text-purple-400 font-medium mb-2">We ensure:</p>
          <ul className="space-y-2 text-slate-300">
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Correct Sharia-compliant structure</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Transparent cost comparisons</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Clear explanation of agreements</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Full UK regulatory compliance</li>
          </ul>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur border border-emerald-500/30 rounded-2xl p-6 mb-6 text-center"
        >
          <h2 className="text-2xl font-bold mb-2">Book a Confidential Consultation</h2>
          <p className="text-slate-300 mb-4">Private, respectful and adviser-led guidance.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href="tel:07550199399" className="block">
              <Button className="w-full h-14 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-lg">
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
            <Link to="/book-appointment" className="block">
              <Button className="w-full h-14 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-lg">
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Consultation
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition text-sm">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Home className="w-3 h-3 text-white" />
            </div>
            Thames Mortgage & Protection
          </Link>
          <p className="text-xs text-slate-500 mt-3">FCA Regulated • FRN 1045227</p>
          <p className="text-xs text-amber-500 mt-2 font-medium">Your home may be repossessed if you do not keep up repayments on your mortgage or loans secured on it.</p>
        </motion.div>
      </div>
    </div>
  );
}
